import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { UserRegDetails } from 'src/app/models/user';
import { EncryptionService } from '../Encryption/encryption.service';
import { v4 as uuidv4 } from 'uuid';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { CyclopsHttp } from 'src/app/services/app.http';
export interface LocationData {
  latitude: number;
  longitude: number;
  address: string;
  formatted_address?: string;
}

export interface EmergencySOSData {
  locationType: 'current' | 'custom' | 'another_user';
  location: LocationData;
  selectedUser?: UserRegDetails;
  notes?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private geocoder?: google.maps.Geocoder;

  constructor(
    private encryption: EncryptionService,
    private db: AngularFireDatabase,
    private http: CyclopsHttp
  ) {
    this.initializeGeocoder();
  }

  private async initializeGeocoder(): Promise<void> {
    try {
      // Wait for Google Maps to be available
      await this.waitForGoogleMaps();

      // Import the geocoding library
      const { Geocoder } = (await google.maps.importLibrary(
        'geocoding'
      )) as google.maps.GeocodingLibrary;

      this.geocoder = new Geocoder();
      console.log('Geocoder initialized successfully');
    } catch (error) {
      console.error('Failed to initialize geocoder:', error);
    }
  }

  private async waitForGoogleMaps(): Promise<void> {
    return new Promise((resolve) => {
      const checkGoogle = () => {
        if (
          typeof google !== 'undefined' &&
          google.maps &&
          google.maps.importLibrary
        ) {
          resolve();
        } else {
          setTimeout(checkGoogle, 100);
        }
      };
      checkGoogle();
    });
  }

  private async ensureGeocoderReady(): Promise<google.maps.Geocoder> {
    if (!this.geocoder) {
      await this.initializeGeocoder();
    }

    if (!this.geocoder) {
      throw new Error('Geocoder not available');
    }

    return this.geocoder;
  }

  getCurrentLocation(isAddressRequired = true): Observable<LocationData> {
    return from(
      new Promise<LocationData>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject({
            code: 0,
            message: 'Geolocation is not supported by this browser',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3,
          });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            try {
              let address;
              if (isAddressRequired) {
                address = await this.reverseGeocode(lat, lng);
              }

              resolve({
                latitude: lat,
                longitude: lng,
                address: address,
              });
            } catch (error) {
              console.warn(
                'Reverse geocoding failed, using coordinates:',
                error
              );
              resolve({
                latitude: lat,
                longitude: lng,
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              });
            }
          },
          (error) => {
            console.error('Geolocation error:', error);

            // Enhanced error object with code and message
            const enhancedError = {
              code: error.code,
              message: error.message,
              PERMISSION_DENIED: 1,
              POSITION_UNAVAILABLE: 2,
              TIMEOUT: 3,
              originalError: error,
            };

            reject(enhancedError);
          },
          {
            enableHighAccuracy: true,
            timeout: 15000, // Increased timeout
            maximumAge: 60000,
          }
        );
      })
    );
  }

  async reverseGeocode(lat: number, lng: number): Promise<string> {
    try {
      const geocoder = await this.ensureGeocoderReady();

      return new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          console.log('Reverse geocoding status:', status);
          console.log('Reverse geocoding results:', results);

          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            resolve(results[0].formatted_address);
          } else {
            console.warn('Reverse geocoding failed:', status);
            reject(`Reverse geocoding failed: ${status}`);
          }
        });
      });
    } catch (error) {
      console.error('Error in reverseGeocode:', error);
      throw error;
    }
  }

  async geocodeAddress(address: string): Promise<LocationData> {
    try {
      console.log('Geocoding address:', address);
      const geocoder = await this.ensureGeocoderReady();

      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: address.trim() }, (results, status) => {
          console.log('Geocoding status:', status);
          console.log('Geocoding results:', results);

          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            const result = results[0];
            const location = result.geometry.location;

            const locationData: LocationData = {
              latitude: location.lat(),
              longitude: location.lng(),
              address: result.formatted_address,
              formatted_address: result.formatted_address,
            };

            console.log('Successfully geocoded to:', locationData);
            resolve(locationData);
          } else {
            console.warn('Geocoding failed:', status, 'for address:', address);

            // Provide more specific error messages
            let errorMessage = 'Address not found';
            switch (status) {
              case google.maps.GeocoderStatus.ZERO_RESULTS:
                errorMessage = 'No results found for this address';
                break;
              case google.maps.GeocoderStatus.OVER_QUERY_LIMIT:
                errorMessage = 'Geocoding quota exceeded';
                break;
              case google.maps.GeocoderStatus.REQUEST_DENIED:
                errorMessage = 'Geocoding request denied';
                break;
              case google.maps.GeocoderStatus.INVALID_REQUEST:
                errorMessage = 'Invalid geocoding request';
                break;
              case google.maps.GeocoderStatus.UNKNOWN_ERROR:
                errorMessage = 'Unknown geocoding error';
                break;
            }

            reject(new Error(errorMessage));
          }
        });
      });
    } catch (error) {
      console.error('Error in geocodeAddress:', error);
      throw error;
    }
  }

  // Alternative method using Places API for better search results
  async searchPlaces(query: string): Promise<LocationData[]> {
    try {
      await this.waitForGoogleMaps();

      // Import Places library
      const { PlacesService } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;

      // Create a temporary map element for PlacesService
      const mapDiv = document.createElement('div');
      const map = new google.maps.Map(mapDiv);
      const service = new PlacesService(map);

      return new Promise((resolve, reject) => {
        const request: google.maps.places.TextSearchRequest = {
          query: query.trim(),
        };

        service.textSearch(request, (results, status) => {
          console.log('Places search status:', status);
          console.log('Places search results:', results);

          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            results &&
            results.length > 0
          ) {
            const locations: LocationData[] = results
              .filter((place) => place.geometry && place.geometry.location)
              .map((place) => ({
                latitude: place.geometry!.location!.lat(),
                longitude: place.geometry!.location!.lng(),
                address:
                  place.formatted_address || place.name || 'Unknown location',
                formatted_address: place.formatted_address,
              }));

            resolve(locations);
          } else {
            console.warn('Places search failed:', status);
            reject(new Error('Places search failed'));
          }
        });
      });
    } catch (error) {
      console.error('Error in searchPlaces:', error);
      throw error;
    }
  }
  async sendSOSFromFloatButton(requestData?: EmergencySOSData): Promise<void> {
    let userId;
    let currentUser = this.encryption.getCurrentUser();
    if (requestData.locationType === 'current') {
      userId = currentUser.userID;
    } else if (requestData.locationType === 'another_user') {
      userId = requestData.selectedUser.userId;
    } else {
      return;
    }
    const userDetails = {
      userId:
        requestData.locationType === 'current'
          ? currentUser.userID
          : requestData.selectedUser.userId,
      userName:
        requestData.locationType === 'current'
          ? currentUser.fullName
          : requestData.selectedUser.firstname +
            ' ' +
            requestData.selectedUser.lastname,
      userPhone:
        requestData.locationType === 'current'
          ? currentUser.phoneNumber
          : requestData.selectedUser.phoneNumber,
    };

    const sosRequestId = uuidv4();
    const sosData = {
      requestId: sosRequestId,
      latitude: requestData.location.latitude,
      longitude: requestData.location.longitude,
      lastUpdated: Date.now(),
      isActive: true,
      isSOS: true,
      sosTimestamp: Date.now(),
      locationType: requestData.locationType,
      userId: userId,
    };

    const path = `LocationSos/${currentUser.organizationID}/ActiveUsers/${userId}`;
    await this.db.object(path).update(sosData);

    // Also create SOS alert record
    const sosAlertPath = `LocationSos/${currentUser.organizationID}/SOSAlerts/${sosRequestId}`;

    await this.db.object(sosAlertPath).set({
      ...sosData,
      userId: userDetails.userId,
      userName: userDetails.userName,
      userPhone: userDetails.userPhone,
      organizationId: currentUser.organizationID,
      alertId: `SOS_${Date.now()}_${userDetails.userId}`,
    });

    await this.sendSOSToBackend(sosData);
  }
  private async sendSOSToBackend(sosData: any): Promise<void> {
    try {
      // Prepare API payload
      const apiPayload = {
        requestId: sosData.requestId,
        latitude: sosData.latitude,
        longitude: sosData.longitude,
        locationType: sosData.locationType,
        userId: sosData.userId,
      };

      // Make API call to your backend
      const response = await firstValueFrom(
        this.http.post('CreateSOSRequest', apiPayload)
      );

      console.log('SOS sent to backend successfully:', response);
    } catch (error) {
      console.error('Failed to send SOS to backend:', error);
      // Don't throw error here - Firebase storage is the primary backup
      // Log the error but don't fail the entire SOS process
    }
  }
}
